import { useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { Button, Stack, Box, Divider, Typography, List, ListItem, ListItemText } from '@mui/material';
import {
  CheckCircleOutline,
  FileUploadOutlined,
  InventoryOutlined,
  FolderOffOutlined,
  PendingActionsOutlined,
  EventNoteOutlined,
  LaunchOutlined,
  PhoneCallbackOutlined,
} from '@mui/icons-material';
import { useTranslations } from '@/i18n/use-translations';
import { useBreakpointUp } from '@/hooks/use-breakpoint';
import type { ContributorRoleSummaryDto } from '@/types/dtos/contributors/response/contributor-summary.dto';
import type { ContributorSummaryDto } from '@/types/dtos/contributors/response/contributor-summary.dto';
import type { EventSummaryDto } from '@/types/dtos/events/response/event-summary.dto';
import type { FlowSummaryDto, FlowSlotSummaryDto } from '@/types/dtos/flows/response/flow-summary.dto';
import type { SessionSummaryDto } from '@/types/dtos/sessions/response/session-summary.dto';
import type { ContributorRoleSlotSummaryDto } from '@/types/dtos/contributors/response/contributor-summary.dto';
import type { EventSlotDto } from '@/types/dtos/events/response/event.dto';
import StandardModal from '@/components/common/standard.modal';
import { MediaWidget } from '@/components/events/detail/tabs/common/media/media.widget';
import { FlowPlaylistMediaWidget } from '@/components/flows/detail/edit-details/flow-playlist-media-widget';
import { SessionPlaylistMediaWidget } from '@/components/sessions/session-playlist-media-widget';
import { useGetSessionQuery } from '@/state/queries/sessions/use-get-session';
import type { MediaListContext } from '@/components/events/detail/tabs/common/media/media.list';
import type { MediaDto } from '@/types/dtos/media/media.dto';
import { slotNeedsRemoteSpeakerConfirmation } from '@/utils/remote-speaker-status.utils';
import { EVENT_CREW_ROLES } from '@/types/dtos/contributors/response/contributor.dto';
import { useContributorSummaryQuery } from '@/state/queries/contributors/user-contributors';
import { useGetFlowQuery } from '@/state/queries/flows/use-get-flow';
import { minuteOffsetToTime } from '@/components/events/helpers/minute-offset-to-time.helper';
import {
  mediaCanUpload,
  mergeRoleMedia,
  roleMediaNeedsContributorUploadAction,
} from '@/components/user-dashboard/contributions/contributor-role-media.utils.ts';

const canUploadToMedia = (media: MediaDto): boolean => mediaCanUpload(media);

/** Media rows the API returned with Oso read permission (visibility is enforced server-side). */
const canReadMedia = (media: MediaDto): boolean => media.permissions?.includes('read') ?? false;

const buildEventManagementUrl = (event?: EventSummaryDto): string | null => {
  if (!event?.id) return null;

  let url = `/event/${event.id}`;
  if (!event.multiFlow && event.flows?.length) {
    const singleFlow = event.flows[0];
    if (singleFlow) url = `/flow/${singleFlow.id}`;
  }

  return url;
};

const RoleDetailsModal = ({
  open,
  onClose,
  role,
  contributor,
  event,
  flows,
  sessions,
  remoteSpeakershipEnabled,
}: {
  open: boolean;
  onClose: () => void;
  role: ContributorRoleSummaryDto | null;
  contributor: ContributorSummaryDto;
  event?: EventSummaryDto;
  flows: FlowSummaryDto[];
  sessions: SessionSummaryDto[];
  remoteSpeakershipEnabled: boolean;
}) => {
  const t = useTranslations();
  const navigate = useNavigate();
  const isWideScreen = useBreakpointUp('md');
  const { data: freshContributor } = useContributorSummaryQuery(contributor.id);

  const currentContributor = freshContributor || contributor;

  const effectiveFlows = currentContributor.flows ?? flows;
  const effectiveSessions = currentContributor.sessions ?? sessions;

  const flowsById = useMemo(() => {
    const map = new Map<string, FlowSummaryDto>();
    for (const flow of effectiveFlows) {
      map.set(flow.id, flow);
      if (flow.origin) {
        map.set(flow.origin, flow);
      }
    }
    return map;
  }, [effectiveFlows]);
  const sessionsById = useMemo(
    () => new Map(effectiveSessions.map((session) => [session.id, session])),
    [effectiveSessions],
  );

  const currentRole = useMemo(() => {
    if (!role) return null;
    return currentContributor.roles?.find((r) => r.id === role.id) || role;
  }, [role, currentContributor.roles]);

  const isEventCrewRole = useMemo(() => {
    if (!currentRole || currentRole.entityType !== 'Event') {
      return false;
    }
    return (EVENT_CREW_ROLES as readonly string[]).includes(currentRole.role);
  }, [currentRole]);

  const flowRoleOriginId =
    open && currentRole?.entityType === 'Flow' && currentRole.entityId ? currentRole.entityId : undefined;

  const { data: fullFlow } = useGetFlowQuery(flowRoleOriginId, undefined, {
    syncToStore: false,
    mode: 'ORGANIZE',
  });

  const sessionRoleOriginId =
    open && currentRole?.entityType === 'Session' && currentRole.entityId ? currentRole.entityId : undefined;

  const { data: fullSession } = useGetSessionQuery(sessionRoleOriginId, 'ORGANIZE');

  const roleFlow = useMemo(() => {
    if (!currentRole?.entityId || currentRole.entityType !== 'Flow') {
      return undefined;
    }
    if (fullFlow) {
      return fullFlow;
    }
    return flowsById.get(currentRole.entityId);
  }, [currentRole, flowsById, fullFlow]);

  const roleSession = useMemo(() => {
    if (!currentRole?.entityId || currentRole.entityType !== 'Session') {
      return undefined;
    }
    if (fullSession) {
      return fullSession;
    }
    return sessionsById.get(currentRole.entityId);
  }, [currentRole, sessionsById, fullSession]);

  const roleMedia = useMemo(() => {
    if (!currentRole) return [];

    let entityMedia: MediaDto[] = [];
    if (currentRole.entityType === 'Session' && currentRole.entityId) {
      entityMedia = sessionsById.get(currentRole.entityId)?.media ?? [];
    } else if (currentRole.entityType === 'Flow' && currentRole.entityId) {
      entityMedia = flowsById.get(currentRole.entityId)?.media ?? [];
    }

    return mergeRoleMedia({
      role: currentRole,
      contributorMedia: currentContributor.media ?? [],
      entityMedia,
      eventId: event?.id,
    }) as MediaDto[];
  }, [currentRole, currentContributor.media, event?.id, flowsById, sessionsById]);

  const readableMedia = useMemo(() => roleMedia.filter(canReadMedia), [roleMedia]);
  const uploadableMedia = useMemo(() => roleMedia.filter(canUploadToMedia), [roleMedia]);

  const mediaStatus = useMemo(() => {
    if (isEventCrewRole) return 'eventCrewMedia';

    if (!currentRole?.acceptedAt) return 'pendingConfirmation';

    if (readableMedia.length === 0) return 'noMedia';

    const { hasOpenRequested, hasOwnMissing } = roleMediaNeedsContributorUploadAction(uploadableMedia);
    if (hasOpenRequested) return 'requestsOpen';
    if (hasOwnMissing) return 'ownMissingAssets';

    return 'allComplete';
  }, [readableMedia, uploadableMedia, currentRole?.acceptedAt, isEventCrewRole]) as
    | 'eventCrewMedia'
    | 'pendingConfirmation'
    | 'noMedia'
    | 'requestsOpen'
    | 'ownMissingAssets'
    | 'allComplete';

  const mediaStatusConfig = useMemo(() => {
    const configs = {
      eventCrewMedia: { icon: <EventNoteOutlined color="info" />, key: 'EventCrewMedia' },
      pendingConfirmation: { icon: <PendingActionsOutlined color="primary" />, key: 'PendingConfirmation' },
      noMedia: { icon: <FolderOffOutlined color="disabled" />, key: 'NoMedia' },
      requestsOpen: { icon: <FileUploadOutlined color="primary" />, key: 'RequestsOpen' },
      ownMissingAssets: { icon: <InventoryOutlined color="primary" />, key: 'OwnMissingAssets' },
      allComplete: { icon: <CheckCircleOutline color="success" />, key: 'AllComplete' },
    };
    return configs[mediaStatus];
  }, [mediaStatus]);

  const roleAccepted = !!currentRole?.acceptedAt;
  const showProminentMediaMessage = isEventCrewRole || !roleAccepted;
  const useColumnMediaLayout = isWideScreen || isEventCrewRole;

  const eventForManagementLink = event ?? contributor.event;
  const eventManagementUrl = useMemo(() => buildEventManagementUrl(eventForManagementLink), [eventForManagementLink]);

  const handleOpenEventManagement = useCallback(() => {
    if (!eventManagementUrl) return;
    onClose();
    navigate(eventManagementUrl);
  }, [eventManagementUrl, onClose, navigate]);

  const findEventSlot = useCallback(
    (eventSlotId: string | undefined) => {
      if (!event || !eventSlotId) return undefined;
      return event.slots?.find((slot) => slot.id === eventSlotId || slot.origin === eventSlotId);
    },
    [event],
  );

  const getFlowSlotTimingSummary = (
    currentEvent: EventSummaryDto,
    flowSlotInEvent: EventSlotDto,
    contentSlotInFlow: FlowSlotSummaryDto,
  ) => {
    if (!flowSlotInEvent.day || !currentEvent.days) {
      return t('myEventsList.contributionsList.slotWithoutDescription');
    }
    const day = currentEvent.days.find((d) => d.id === flowSlotInEvent.day.id);
    if (!day) return t('myEventsList.contributionsList.slotWithoutDescription');
    const dayNumber = currentEvent.days.indexOf(day) + 1;
    if (!currentEvent.startDate) return `Day ${dayNumber}`;
    const slotOffsetMinutes =
      contentSlotInFlow?.computedOffsetMinutes ?? contentSlotInFlow?.plannedOffsetMinutes ?? null;
    if (slotOffsetMinutes === null) return `Day ${dayNumber}`;
    const slotStartOffsetFromEventStart = flowSlotInEvent.startOffsetMinutes + slotOffsetMinutes;
    const slotStartTime = minuteOffsetToTime(slotStartOffsetFromEventStart, currentEvent.startDate);
    const slotDuration = contentSlotInFlow?.computedDurationMinutes ?? contentSlotInFlow?.plannedDurationMinutes;
    if (slotDuration === undefined) return `Day ${dayNumber} | ${slotStartTime}`;
    const slotEndTime = minuteOffsetToTime(slotStartOffsetFromEventStart + slotDuration, currentEvent.startDate);
    return `Day ${dayNumber} | ${slotStartTime} - ${slotEndTime}`;
  };

  const getRoleSlotTimingText = (roleSlot: ContributorRoleSlotSummaryDto) => {
    if (!event?.slots || !event.days) {
      return t('myEventsList.contributionsList.slotWithoutDescription');
    }

    if (roleSlot.entityType === 'FlowSlot') {
      const entityId = roleSlot.entityId;
      if (!entityId) return t('myEventsList.contributionsList.slotWithoutDescription');
      const flowId = roleSlot.parentEntity;
      const flow = flowId ? flowsById.get(flowId) : undefined;
      const contentSlotInFlow = flow?.slots?.find((slot) => slot.id === entityId || slot.origin === entityId);
      const flowSlotInEvent =
        findEventSlot(roleSlot.eventSlotId) ||
        (flowId ? event.slots.find((slot) => slot.content === flowId || slot.origin === flowId) : undefined) ||
        event.slots.find((slot) => slot.content === entityId || slot.origin === entityId);

      if (flowSlotInEvent && contentSlotInFlow) {
        return getFlowSlotTimingSummary(event, flowSlotInEvent, contentSlotInFlow);
      }
      return t('myEventsList.contributionsList.slotWithoutDescription');
    }

    if (roleSlot.entityType === 'EventSlot') {
      const eventSlot = findEventSlot(roleSlot.entityId) || findEventSlot(roleSlot.eventSlotId);
      if (!eventSlot?.day || typeof eventSlot.startOffsetMinutes !== 'number') {
        return t('myEventsList.contributionsList.slotWithoutDescription');
      }
      const dayIndex = event.days.findIndex((day) => day.id === eventSlot.day.id);
      if (dayIndex === -1) return t('myEventsList.contributionsList.slotWithoutDescription');
      const dayNumber = dayIndex + 1;
      const startTime = minuteOffsetToTime(eventSlot.startOffsetMinutes, event.startDate);
      const durationMinutes = eventSlot.durationPlannedMinutes;
      if (!durationMinutes) return `Day ${dayNumber} | ${startTime}`;
      const endTime = minuteOffsetToTime(eventSlot.startOffsetMinutes + durationMinutes, event.startDate);
      return `Day ${dayNumber} | ${startTime} - ${endTime}`;
    }

    if (roleSlot.entityType === 'Flow') {
      const flowId = roleSlot.entityId;
      if (!flowId) return t('myEventsList.contributionsList.slotWithoutDescription');
      const flow = flowsById.get(flowId);
      return flow?.title || t('myEventsList.contributionsList.slotWithoutDescription');
    }

    return t('myEventsList.contributionsList.slotWithoutDescription');
  };

  const getRoleSlotTitle = useCallback(
    (roleSlot: ContributorRoleSlotSummaryDto) => {
      if (roleSlot.entityType !== 'FlowSlot') return undefined;
      const entityId = roleSlot.entityId;
      if (!entityId) return undefined;
      const flowId = roleSlot.parentEntity;
      const flow = flowId ? flowsById.get(flowId) : undefined;
      if (flow?.title) return flow.title;
      const contentSlotInFlow = flow?.slots?.find((slot) => slot.id === entityId || slot.origin === entityId);
      if (!contentSlotInFlow) return undefined;
      if (contentSlotInFlow.title) return contentSlotInFlow.title;
      const content = contentSlotInFlow.content;
      if (typeof content === 'string') {
        return sessionsById.get(content)?.title;
      }
      return content?.title;
    },
    [flowsById, sessionsById],
  );

  if (!currentRole) return null;

  const roleLabel = t(`common.roleLabels.${currentRole.role}`);

  const entityTitle = (() => {
    if (currentRole.entityType === 'Event') {
      return event?.title || t('myEventsList.unknown');
    }
    if (!currentRole.entityId) return t('myEventsList.unknown');
    if (currentRole.entityType === 'Session') {
      return roleSession?.title || sessionsById.get(currentRole.entityId)?.title || t('myEventsList.unknown');
    }
    if (currentRole.entityType === 'Flow') {
      return roleFlow?.title || flowsById.get(currentRole.entityId)?.title || t('myEventsList.unknown');
    }
    return t('myEventsList.unknown');
  })();

  const entityTypeLabel = (() => {
    if (currentRole.entityType === 'Event') {
      return event?.type ? t(`event.types.${event.type}`) : t('user.contributionsList.entityTypeEvent');
    }
    if (currentRole.entityType === 'Session') {
      return t('common.session');
    }
    if (currentRole.entityType === 'Flow') {
      return t('common.flow');
    }
    return currentRole.entityType;
  })();

  const entityDescription = (() => {
    if (currentRole.entityType === 'Event') {
      return event?.description?.trim() || '';
    }
    if (currentRole.entityType === 'Session') {
      return fullSession?.description?.trim() || '';
    }
    if (currentRole.entityType === 'Flow') {
      return fullFlow?.description?.trim() || '';
    }
    return '';
  })();

  const parentId = currentRole.entityId || '';
  const parentType =
    currentRole.entityType === 'Event' ? 'Event' : currentRole.entityType === 'Session' ? 'Session' : 'Flow';
  const context = (
    currentRole.entityType === 'Event' ? 'Event' : currentRole.entityType === 'Session' ? 'Session' : 'Flow'
  ) as MediaListContext;

  const eventManagementButton =
    isEventCrewRole && eventManagementUrl ? (
      <Button
        variant="contained"
        color="info"
        size="medium"
        startIcon={<LaunchOutlined />}
        onClick={handleOpenEventManagement}
        sx={{ mt: 2.5 }}
      >
        {t('user.contributionsList.openEventManagement')}
      </Button>
    ) : null;

  const mediaStatusMessage = useColumnMediaLayout ? (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        py: showProminentMediaMessage ? 4 : 3,
        px: 3,
        textAlign: 'center',
        ...(showProminentMediaMessage ? { flex: 1, justifyContent: 'center', minHeight: 160 } : {}),
      }}
    >
      <Box sx={{ 'mb': 1, '& .MuiSvgIcon-root': { fontSize: showProminentMediaMessage ? 40 : 36 } }}>
        {mediaStatusConfig.icon}
      </Box>
      <Typography variant={showProminentMediaMessage ? 'body1' : 'body2'} color="text.secondary">
        {t(`user.contributionsList.mediaStatus${mediaStatusConfig.key}`)}
      </Typography>
      <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5, maxWidth: 360 }}>
        {t(`user.contributionsList.mediaStatus${mediaStatusConfig.key}Subtitle`)}
      </Typography>
      {eventManagementButton}
    </Box>
  ) : (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ px: 2, py: 1.5 }}>
      <Box sx={{ '& .MuiSvgIcon-root': { fontSize: 24 }, 'display': 'flex' }}>{mediaStatusConfig.icon}</Box>
      <Stack spacing={0}>
        <Typography variant="body2" color="text.secondary">
          {t(`user.contributionsList.mediaStatus${mediaStatusConfig.key}`)}
        </Typography>
        <Typography variant="caption" color="text.disabled">
          {t(`user.contributionsList.mediaStatus${mediaStatusConfig.key}Subtitle`)}
        </Typography>
      </Stack>
    </Stack>
  );

  const slots = currentRole.slots ?? [];

  return (
    <StandardModal
      open={open}
      close={onClose}
      title={roleLabel}
      subtitle={entityTitle}
      menu={false}
      size="lg"
      height={isWideScreen ? '700px' : '500px'}
      maxHeight={isWideScreen ? 'calc(100vh - 64px)' : 'calc(100vh - 32px)'}
      width={isWideScreen ? '1000px' : undefined}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: isWideScreen ? 'row' : 'column',
          gap: isWideScreen ? 0 : 2,
          height: isWideScreen ? '100%' : 'auto',
          width: '100%',
          minHeight: 0,
          ...(isWideScreen && { overflow: 'hidden' }),
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: isWideScreen ? '100%' : 'auto',
            width: isWideScreen ? 'calc(50% - 1px)' : '100%',
            flexShrink: 0,
            minWidth: 0,
            ...(isWideScreen && { minHeight: 0, overflow: 'hidden' }),
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 2,
              ...(isWideScreen && {
                flex: 1,
                minHeight: 0,
                overflow: 'auto',
              }),
            }}
          >
            <Stack spacing={1.5}>
              <Stack spacing={0.5}>
                <Typography variant="h6" component="h3">
                  {entityTitle}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {entityTypeLabel}
                </Typography>
                {entityDescription ? (
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                    {entityDescription}
                  </Typography>
                ) : null}
              </Stack>

              {slots.length > 0 && (
                <>
                  <Divider />
                  <Typography variant="subtitle2">{t('user.contributionsList.invitedSlots')}</Typography>
                  <List dense disablePadding>
                    {slots.map((slot) => {
                      const timingText = getRoleSlotTimingText(slot);
                      const slotTitle = getRoleSlotTitle(slot);
                      const needsRemote = remoteSpeakershipEnabled && slotNeedsRemoteSpeakerConfirmation(slot);
                      const isConfirmed = !!slot.acceptedAt && !needsRemote;
                      const isRemotePending = !!slot.acceptedAt && needsRemote;

                      return (
                        <ListItem
                          key={slot.id}
                          dense
                          secondaryAction={
                            isConfirmed ? (
                              <CheckCircleOutline color="success" />
                            ) : (
                              <PendingActionsOutlined color="primary" />
                            )
                          }
                          sx={{ bgcolor: 'background.elevation2', borderRadius: 1, mb: 0.5 }}
                        >
                          <ListItemText
                            primary={
                              <Stack direction="row" spacing={0.5} alignItems="center" component="span">
                                <Typography variant="body2" component="span">
                                  {timingText}
                                </Typography>
                                {slot.isRemoteSpeaker && remoteSpeakershipEnabled ? (
                                  <PhoneCallbackOutlined
                                    color={slot.remoteSpeakerAcceptedAt ? 'success' : 'primary'}
                                    sx={{ fontSize: 16 }}
                                  />
                                ) : null}
                              </Stack>
                            }
                            secondary={
                              isRemotePending ? (
                                <Stack spacing={0.25} component="span">
                                  {slotTitle ? (
                                    <Typography variant="caption" color="text.secondary" component="span">
                                      {slotTitle}
                                    </Typography>
                                  ) : null}
                                  <Typography variant="caption" color="text.secondary" component="span">
                                    {t('user.contributionsList.speakingRemotely')}
                                  </Typography>
                                </Stack>
                              ) : (
                                slotTitle
                              )
                            }
                            secondaryTypographyProps={isRemotePending ? { component: 'div' } : undefined}
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                </>
              )}
            </Stack>
          </Box>
        </Box>
        {currentRole.entityId && (isWideScreen ? <Divider orientation="vertical" flexItem /> : <Divider />)}
        {currentRole.entityId && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: isWideScreen ? '100%' : 'auto',
              width: isWideScreen ? 'calc(50% - 1px)' : '100%',
              flexShrink: 0,
              minWidth: 0,
              ...(isWideScreen && {
                minHeight: 0,
                overflow: 'auto',
              }),
            }}
          >
            {roleAccepted && !isEventCrewRole ? (
              <Box
                sx={{
                  ...(isWideScreen && {
                    flexShrink: 0,
                  }),
                }}
              >
                {roleFlow ? (
                  <Stack spacing={2}>
                    <FlowPlaylistMediaWidget
                      flow={roleFlow}
                      scope="flow_intro"
                      disableBorder
                      disableBackground
                      disableHover
                      disableShadow
                      hideMediaSettingsSwitches
                      requestButtonLabel="SIGNAL FOR LATER"
                      requestSubmitLabel="SUBMIT"
                      disablePlayerQuery
                    />
                    <FlowPlaylistMediaWidget
                      flow={roleFlow}
                      scope="flow_transition"
                      disableBorder
                      disableBackground
                      disableHover
                      disableShadow
                      hideMediaSettingsSwitches
                      requestButtonLabel="SIGNAL FOR LATER"
                      requestSubmitLabel="SUBMIT"
                      disablePlayerQuery
                    />
                    <FlowPlaylistMediaWidget
                      flow={roleFlow}
                      scope="flow_resume"
                      disableBorder
                      disableBackground
                      disableHover
                      disableShadow
                      hideMediaSettingsSwitches
                      requestButtonLabel="SIGNAL FOR LATER"
                      requestSubmitLabel="SUBMIT"
                      disablePlayerQuery
                    />
                    <FlowPlaylistMediaWidget
                      flow={roleFlow}
                      scope="flow_outro"
                      disableBorder
                      disableBackground
                      disableHover
                      disableShadow
                      hideMediaSettingsSwitches
                      requestButtonLabel="SIGNAL FOR LATER"
                      requestSubmitLabel="SUBMIT"
                      disablePlayerQuery
                    />
                  </Stack>
                ) : roleSession ? (
                  <SessionPlaylistMediaWidget
                    session={roleSession}
                    context="Session"
                    disableBorder
                    disableBackground
                    disableHover
                    disableShadow
                    hideMediaSettingsSwitches
                    requestButtonLabel="SIGNAL FOR LATER"
                    requestSubmitLabel="SUBMIT"
                    disablePlayerQuery
                  />
                ) : (
                  <MediaWidget
                    media={readableMedia}
                    parentId={parentId}
                    parentType={parentType}
                    contextParentId={parentId}
                    contextParentType={parentType}
                    entityId={currentRole.entityId}
                    context={context}
                    disableBorder={true}
                    disableBackground={true}
                    disableHover={true}
                    disableShadow={true}
                    scrollableList={false}
                    requestButtonLabel="SIGNAL FOR LATER"
                    requestSubmitLabel="SUBMIT"
                    disablePlayerQuery={true}
                    contributorFacing
                    contributorCanAdd={false}
                    showRequestButton={false}
                  />
                )}
              </Box>
            ) : null}
            {mediaStatusMessage}
          </Box>
        )}
      </Box>
    </StandardModal>
  );
};

export default RoleDetailsModal;
